import { stripHtmlToPlainText } from '../../../shared/html-text.js';
import {
  productCartItemAvailabilityMode,
  productCartItemFeatureUsps,
  getProductPriceTierParts
} from '../product-cart-item.config.js';

/** Hero-volgorde: dag eerst; anders eerstvolgende periode met juiste suffix. */
const FROM_PREFERENCE = [
  'price',
  'per_weekend',
  'per_halve_dag',
  'per_uur',
  'price2',
  'per_4_weken',
  'per_stuk',
  'per_4_stuk',
  'per_set',
  'per_meter',
  'per_bundel'
];

const FROM_SUFFIX_BY_KEY = {
  price: 'per dag',
  per_halve_dag: 'per halve dag',
  per_weekend: 'per weekend',
  per_uur: 'per uur',
  price2: 'per week',
  per_4_weken: 'per 4 weken',
  per_stuk: 'per stuk',
  per_4_stuk: 'per 4 stuk',
  per_set: 'per set',
  per_meter: 'per meter',
  per_bundel: 'per bundel'
};

function normalizePriceTierKey(label) {
  const l = String(label || '').toLowerCase();
  if (!l) return '';
  if (l.includes('weekend')) return 'per_weekend';
  if (l.includes('halve')) return 'per_halve_dag';
  if (/\buur\b/.test(l)) return 'per_uur';
  if (l.includes('4 wk') || l.includes('4 week')) return 'per_4_weken';
  if (/\bweek\b/.test(l)) return 'price2';
  if (l.includes('stuk') && l.includes('4')) return 'per_4_stuk';
  if (l.includes('stuk')) return 'per_stuk';
  if (l.includes('set')) return 'per_set';
  if (l.includes('meter')) return 'per_meter';
  if (l.includes('bundel')) return 'per_bundel';
  if (/\bdag\b/.test(l) && !l.includes('halve')) return 'price';
  return '';
}

function suffixForTierKey(key, label) {
  if (key && FROM_SUFFIX_BY_KEY[key]) return FROM_SUFFIX_BY_KEY[key];
  const l = String(label || '').toLowerCase();
  if (l.includes('weekend')) return 'per weekend';
  if (l.includes('halve')) return 'per halve dag';
  if (l.includes('week')) return 'per week';
  if (l.includes('dag')) return 'per dag';
  if (l.includes('uur')) return 'per uur';
  return 'vanaf';
}

function normalizeMoney(value) {
  return String(value || '')
    .replace(/[€\s]/gu, '')
    .replace(',', '.')
    .replace(/[^\d.]/g, '');
}

/**
 * Hero-prijs: dagprijs indien aanwezig; anders eerstvolgende periode met juiste suffix
 * (bv. weekend → "per weekend", nooit foutief als "per dag").
 */
function resolveFromPrice(rawFrom, priceTiers) {
  const bootAmount = stripHtmlToPlainText(rawFrom?.amountHtml || rawFrom?.amount || '');
  const bootSuffix = String(rawFrom?.suffix || '').trim();
  const bootSuffixNorm = bootSuffix.toLowerCase();
  const tiers = priceTiers || [];

  // Backend gaf al een niet-dag suffix (nieuwe PHP-logica).
  if (bootAmount && bootSuffixNorm && bootSuffixNorm !== 'per dag') {
    return { amount: bootAmount, suffix: bootSuffix };
  }

  // Foutief "per dag": bedrag hoort bij weekend/week/… in de tiers.
  if (bootAmount && bootSuffixNorm === 'per dag') {
    for (const key of FROM_PREFERENCE) {
      if (key === 'price') continue;
      const tier = tiers.find((t) => normalizePriceTierKey(t.label) === key);
      if (tier?.amount && normalizeMoney(tier.amount) === normalizeMoney(bootAmount)) {
        return { amount: bootAmount, suffix: suffixForTierKey(key, tier.label) };
      }
    }
    return { amount: bootAmount, suffix: 'per dag' };
  }

  // Geen hero uit backend: kies eerstvolgende beschikbare periode.
  for (const key of FROM_PREFERENCE) {
    const tier = tiers.find((t) => normalizePriceTierKey(t.label) === key);
    if (tier?.amount) {
      return { amount: tier.amount, suffix: suffixForTierKey(key, tier.label) };
    }
  }

  if (bootAmount) {
    return { amount: bootAmount, suffix: bootSuffix || 'per dag' };
  }

  return { amount: '', suffix: 'per dag' };
}

export function normalizeProductPageBoot(boot) {
  const b = boot && typeof boot === 'object' ? boot : {};
  const product = normalizeProduct(b.product || {});
  return {
    breadcrumbs: Array.isArray(b.breadcrumbs) ? b.breadcrumbs : [],
    product,
    pickupLocations: Array.isArray(b.pickupLocations) ? b.pickupLocations : [],
    defaultPickupLocation: b.defaultPickupLocation || 'culemborg',
    rentalPeriod: b.rentalPeriod || {},
    reserveFormBase: b.reserveFormBase || '/index.php?route=product/form&product_id=',
    rentalPeriodUrlSuffix: b.rentalPeriodUrlSuffix || '',
    phone: b.phone || '0345 - 502615',
    reviewStatus: b.reviewStatus !== false,
    availabilityMode: b.availabilityMode || productCartItemAvailabilityMode,
    labels: b.labels && typeof b.labels === 'object' ? b.labels : {},
    trustCards: Array.isArray(b.trustCards) ? b.trustCards : [],
    showRentalDates: b.showRentalDates !== false
  };
}

export function normalizeProduct(raw) {
  const p = raw && typeof raw === 'object' ? raw : {};
  const images = Array.isArray(p.images) ? p.images.filter(Boolean) : p.thumb ? [p.thumb] : [];

  const priceTiers = Array.isArray(p.priceTiers)
    ? p.priceTiers.map((t) => {
        const { label, detail } = getProductPriceTierParts(t.label || '');
        return {
          label,
          detail,
          amount: stripHtmlToPlainText(t.amountHtml || t.amount || '')
        };
      })
    : [];

  const fromPrice = resolveFromPrice(p.fromPrice, priceTiers);

  const rating = Number(p.rating);
  const reviewCount = parseInt(p.reviewCount, 10) || 0;

  return {
    productId: p.productId || 0,
    name: p.name || '',
    shortDescription: p.shortDescription || '',
    descriptionHtml: p.descriptionHtml || '',
    images,
    rating: Number.isFinite(rating) ? Math.min(5, Math.max(0, rating)) : 0,
    reviewCount,
    badge: p.badge || (p.isPopular ? 'popular' : ''),
    specs: Array.isArray(p.specs) ? p.specs : [],
    featureUsps: Array.isArray(p.featureUsps) && p.featureUsps.length ? p.featureUsps : productCartItemFeatureUsps,
    fromPrice,
    priceTiers,
    attributes: Array.isArray(p.attributes) ? p.attributes : [],
    stockAvailable: p.stockAvailable !== false,
    model: p.model || ''
  };
}

export function formatReviewLabel(template, count) {
  return String(template || '({count} reviews)').replace('{count}', String(count || 0));
}

export function buildReserveUrl(base, productId, fromDate, toDate, suffix = '') {
  let url = base;
  if (!url && productId) {
    url = `/index.php?route=product/form&product_id=${productId}`;
  }
  if (!url) return '#';

  const joiner = url.includes('?') ? '&' : '?';

  if (fromDate) {
    const params = new URLSearchParams();
    params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    return `${url}${joiner}${params.toString()}`;
  }

  const cleanSuffix = String(suffix || '').replace(/^\?/, '').replace(/^&/, '');
  if (cleanSuffix) {
    return `${url}${joiner}${cleanSuffix}`;
  }

  return url;
}
